import { devLog, devWarn } from './devLog';

/**
 * Driver Token Manager
 *
 * Singleton that manages all token state and refresh logic for the driver app.
 * Supports two auth modes:
 *   - Web: Auth0 getAccessTokenSilently as the token fetcher
 *   - Embedded (WebView): localStorage token injected by native app
 *
 * Key features:
 *   - Deduplication via a single in-flight refreshPromise
 *   - 60-second expiry buffer so tokens are refreshed before they expire
 *   - Proactive refresh at 75% of token lifetime
 *   - Exponential backoff after repeated failures
 *   - Visibility-change handler for web (tab resume)
 *   - No cross-tab sync or Web Worker (not needed for driver app)
 */

type TokenFetcher = (bypassCache?: boolean) => Promise<string>;

interface TokenPayload {
  exp: number;
}

const TOKEN_EXPIRY_BUFFER_MS = 60 * 1000; // 60 seconds before actual expiry
const PROACTIVE_REFRESH_THRESHOLD = 0.75; // Refresh at 75% of token lifetime
const MAX_REFRESH_FAILURES = 3;
const INITIAL_RETRY_DELAY_MS = 5000; // 5 seconds
const TAB_VISIBLE_STALE_THRESHOLD_MS = 2 * 60 * 1000; // 2 minutes

function parseTokenExpiry(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    const payload = JSON.parse(atob(parts[1])) as TokenPayload;
    if (typeof payload.exp === 'number') {
      return payload.exp * 1000;
    }
    return null;
  } catch (err) {
    console.error('[DriverTokenManager] Failed to parse JWT expiry:', err);
    return null;
  }
}

export class DriverTokenManager {
  private tokenFetcher: TokenFetcher | null = null;

  private cachedToken: string | null = null;
  private tokenExpiry: number = 0;

  private refreshPromise: Promise<string | null> | null = null;

  private refreshFailureCount: number = 0;
  private lastRefreshFailure: number = 0;

  private proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  private bypassCacheOnNextRefresh = false;

  /**
   * Initialize with a token fetcher function.
   * For web: wrap Auth0's getAccessTokenSilently.
   * For embedded: wrap a localStorage reader or native bridge call.
   */
  initialize(tokenFetcher: TokenFetcher): void {
    this.tokenFetcher = tokenFetcher;
    devLog('[DriverTokenManager] Initialized');
  }

  /**
   * Returns a valid cached token or refreshes if near/past expiry.
   * All concurrent callers share the same in-flight promise.
   */
  async getToken(): Promise<string | null> {
    if (
      this.cachedToken &&
      Date.now() < this.tokenExpiry - TOKEN_EXPIRY_BUFFER_MS
    ) {
      return this.cachedToken;
    }
    return this.executeRefresh();
  }

  /**
   * Clears the cache and forces a fresh token fetch, bypassing the SDK cache.
   */
  async forceRefresh(): Promise<string | null> {
    devLog('[DriverTokenManager] Force refresh requested');
    this.cachedToken = null;
    this.tokenExpiry = 0;
    this.bypassCacheOnNextRefresh = true;
    return this.executeRefresh();
  }

  /**
   * Call when the browser tab becomes visible again.
   * Only refreshes if the token is stale (expired or within 2 minutes of expiry).
   */
  async handleTabVisible(): Promise<void> {
    const timeUntilExpiry = this.tokenExpiry - Date.now();
    const shouldRefresh =
      timeUntilExpiry <= 0 || timeUntilExpiry <= TAB_VISIBLE_STALE_THRESHOLD_MS;

    if (shouldRefresh) {
      devLog(
        `[DriverTokenManager] Tab visible and token is stale (${Math.round(timeUntilExpiry / 1000)}s remaining), forcing refresh`
      );
      await this.forceRefresh();
    } else {
      devLog(
        `[DriverTokenManager] Tab visible but token is fresh (${Math.round(timeUntilExpiry / 1000)}s remaining), skipping refresh`
      );
    }
  }

  /**
   * Directly update the cached token (used when native app injects a new token).
   */
  updateCachedToken(token: string): void {
    const expiry = parseTokenExpiry(token);
    const now = Date.now();
    this.cachedToken = token;
    this.tokenExpiry = expiry ?? now + 60 * 60 * 1000;
    this.refreshFailureCount = 0;
    devLog('[DriverTokenManager] Cached token updated externally');
    this.scheduleProactiveRefresh(this.tokenExpiry - now);
  }

  private async executeRefresh(): Promise<string | null> {
    if (this.refreshPromise) {
      devLog(
        '[DriverTokenManager] Refresh already in flight, awaiting existing promise'
      );
      return this.refreshPromise;
    }

    const now = Date.now();
    if (this.refreshFailureCount >= MAX_REFRESH_FAILURES) {
      const timeSinceLastFailure = now - this.lastRefreshFailure;
      const backoffDelayMs =
        INITIAL_RETRY_DELAY_MS * Math.pow(2, this.refreshFailureCount - 1);

      if (timeSinceLastFailure < backoffDelayMs) {
        devWarn(
          `[DriverTokenManager] In backoff period (${Math.round((backoffDelayMs - timeSinceLastFailure) / 1000)}s remaining), returning null`
        );
        return null;
      } else {
        devLog(
          '[DriverTokenManager] Backoff period expired, resetting failure count'
        );
        this.refreshFailureCount = 0;
      }
    }

    this.refreshPromise = this.performRefresh();

    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async performRefresh(): Promise<string | null> {
    if (!this.tokenFetcher) {
      devWarn('[DriverTokenManager] Token fetcher not initialized');
      return null;
    }

    devLog('[DriverTokenManager] Starting token refresh');

    try {
      const shouldBypassCache = this.bypassCacheOnNextRefresh;
      this.bypassCacheOnNextRefresh = false;
      const token = await this.tokenFetcher(shouldBypassCache);
      const now = Date.now();
      const expiry = parseTokenExpiry(token);

      this.tokenExpiry = expiry ?? now + 60 * 60 * 1000;
      this.cachedToken = token;
      this.refreshFailureCount = 0;

      const tokenLifetimeMs = this.tokenExpiry - now;
      devLog(
        `[DriverTokenManager] Token refresh successful, expires in ${Math.round(tokenLifetimeMs / 1000 / 60)} minutes`
      );

      this.scheduleProactiveRefresh(tokenLifetimeMs);

      return token;
    } catch (err) {
      console.error('[DriverTokenManager] Token refresh failed:', err);
      this.refreshFailureCount += 1;
      this.lastRefreshFailure = Date.now();
      this.cachedToken = null;
      this.tokenExpiry = 0;
      return null;
    }
  }

  private scheduleProactiveRefresh(tokenLifetimeMs: number): void {
    if (this.proactiveRefreshTimer) {
      clearTimeout(this.proactiveRefreshTimer);
      this.proactiveRefreshTimer = null;
    }

    const refreshDelayMs = tokenLifetimeMs * PROACTIVE_REFRESH_THRESHOLD;
    devLog(
      `[DriverTokenManager] Scheduling proactive refresh in ${Math.round(refreshDelayMs / 1000 / 60)} minutes`
    );

    this.proactiveRefreshTimer = setTimeout(async () => {
      devLog('[DriverTokenManager] Proactive refresh triggered');
      try {
        await this.forceRefresh();
      } catch (err) {
        console.error('[DriverTokenManager] Proactive refresh failed:', err);
      }
    }, refreshDelayMs);
  }

  destroy(): void {
    if (this.proactiveRefreshTimer) {
      clearTimeout(this.proactiveRefreshTimer);
      this.proactiveRefreshTimer = null;
    }
    devLog('[DriverTokenManager] Destroyed');
  }
}

let instance: DriverTokenManager | null = null;

export function getDriverTokenManager(): DriverTokenManager {
  if (!instance) {
    instance = new DriverTokenManager();
  }
  return instance;
}

export function destroyDriverTokenManager(): void {
  if (instance) {
    instance.destroy();
    instance = null;
  }
}

export type { TokenFetcher };
